"use client";

import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslations } from "use-intl";

import { ActiveNotificationIcon, ArrowForwardIcon, CheckIcon } from "@/components/appIcons";
import { BrowserPushRecoveryActions } from "@/components/notifications/BrowserPushRecoveryActions";
import { PwaInstallPromotion } from "@/components/PwaInstallPromotion";
import { Button } from "@/components/ui/button";
import { useWebPushController } from "@/hooks/use-web-push-controller";
import { trackNotificationChannelPromptViewed } from "@/lib/analytics";
import { cn } from "@/lib/utils";

interface BrowserPushControlProps {
  surface: "alerts" | "settings";
}

const POINT_KEYS = ["pointPermission", "pointBackground", "pointAccount"] as const;

type BrowserPushController = ReturnType<typeof useWebPushController>;

function BrowserPushState({
  alerts,
  status,
}: {
  alerts: boolean;
  status: BrowserPushController["status"];
}) {
  const t = useTranslations("browserPush");
  const copy = (() => {
    switch (status) {
      case "unsupported":
        return { title: t("unsupportedTitle"), description: t("unsupportedDescription") };
      case "install_required":
        return { title: t("installTitle"), description: t("installIntroDescription") };
      case "unavailable":
        return { title: t("unavailableTitle"), description: t("unavailableDescription") };
      case "denied":
        return { title: t("deniedTitle"), description: t("deniedDescription") };
      case "active":
        return { title: t("activeTitle"), description: t("activeDescription") };
      case "restore_required":
        return { title: t("restoreTitle"), description: t("restoreDescription") };
      case "unbound":
        return { title: t("errorTitle"), description: t("bindingError") };
      case "error":
        return { title: t("errorTitle"), description: t("errorDescription") };
      default:
        return null;
    }
  })();

  if (!copy) {
    return (
      <p className={cn("text-sm", alerts ? "text-[#c9c4ba]" : "text-muted-foreground")}>
        {status === "preparing" ? t("preparing") : t("currentBrowserInactive")}
      </p>
    );
  }

  return (
    <div role={status === "error" || status === "unbound" ? "alert" : "status"}>
      <p className="font-bold">{copy.title}</p>
      <p
        className={cn(
          "mt-1 text-sm leading-5",
          alerts ? "text-[#c9c4ba]" : "text-muted-foreground",
        )}
      >
        {copy.description}
      </p>
    </div>
  );
}

function BrowserPushSettingsSummary({
  activeBrowserCount,
  status,
  surface,
}: {
  activeBrowserCount: number;
  status: BrowserPushController["status"];
  surface: BrowserPushControlProps["surface"];
}) {
  const t = useTranslations("browserPush");
  if (surface !== "settings" || status === "preparing") return null;
  return (
    <p className="mt-3 text-xs font-semibold text-muted-foreground">
      {status === "active" ? t("currentBrowserActive") : t("currentBrowserInactive")}
      {" · "}
      {t("activeBrowserCount", { count: activeBrowserCount })}
    </p>
  );
}

function EnableButton({
  alerts,
  controller,
}: {
  alerts: boolean;
  controller: BrowserPushController;
}) {
  const t = useTranslations("browserPush");
  if (!controller.canEnable) return null;
  const label = controller.isPending
    ? t("enabling")
    : controller.status === "restore_required"
      ? t("restoreCta")
      : controller.status === "unbound"
        ? t("finishActivationCta")
        : t("enableOutcomeCta");
  return (
    <Button
      className={cn(
        alerts &&
          "min-h-12 w-full gap-3 bg-[#e66a3a] px-5 font-black text-[#17130f] shadow-[0_12px_28px_rgba(230,106,58,.2)] hover:bg-[#f17b4d] focus-visible:ring-[#e66a3a] sm:w-auto",
      )}
      disabled={controller.isPending}
      onClick={() => void controller.enable()}
      size={alerts ? "lg" : "default"}
    >
      <span
        className={cn(
          alerts && "flex size-8 items-center justify-center rounded-md bg-[#17130f]/10",
        )}
        data-icon="inline-start"
      >
        <ActiveNotificationIcon className={cn(alerts ? "size-5" : "size-4")} decorative />
      </span>
      <span>{label}</span>
    </Button>
  );
}

function CurrentBrowserButton({
  alerts,
  controller,
}: {
  alerts: boolean;
  controller: BrowserPushController;
}) {
  const t = useTranslations("browserPush");
  if (!controller.canDisableCurrent) return null;
  return (
    <Button
      className={cn(alerts && "min-h-12 w-full px-5 font-bold sm:w-auto")}
      disabled={controller.isPending}
      onClick={() => void controller.disableCurrent()}
      size={alerts ? "lg" : "default"}
      variant={alerts ? "secondary" : "outline"}
    >
      {controller.isPending ? t("disabling") : t("disableCurrent")}
    </Button>
  );
}

function DisableAllButton({
  controller,
  surface,
}: {
  controller: BrowserPushController;
  surface: BrowserPushControlProps["surface"];
}) {
  const t = useTranslations("browserPush");
  if (surface !== "settings" || controller.viewerType === "visitor") return null;
  return (
    <Button
      disabled={controller.isPending}
      onClick={() => void controller.disableAll()}
      variant="destructive"
    >
      {t("disableAll")}
    </Button>
  );
}

function BrowserPushActions({
  alerts,
  controller,
  surface,
}: {
  alerts: boolean;
  controller: BrowserPushController;
  surface: BrowserPushControlProps["surface"];
}) {
  const t = useTranslations("browserPush");
  const showActions =
    controller.canEnable ||
    controller.status === "active" ||
    controller.status === "preparing" ||
    (surface === "settings" && controller.viewerType !== "visitor");
  if (!showActions) return null;
  return (
    <div className="mt-5 flex flex-wrap gap-2">
      <EnableButton alerts={alerts} controller={controller} />
      {controller.status === "preparing" ? (
        <Button
          className={cn(alerts && "min-h-12 w-full px-5 font-bold sm:w-auto")}
          disabled
          size={alerts ? "lg" : "default"}
        >
          {t("preparing")}
        </Button>
      ) : null}
      <CurrentBrowserButton alerts={alerts} controller={controller} />
      <DisableAllButton controller={controller} surface={surface} />
    </div>
  );
}

function PersonalizeLink({ alerts, status }: { alerts: boolean; status: string }) {
  const t = useTranslations("browserPush");
  if (!alerts || status !== "active") return null;
  return (
    <Link
      className="mt-4 inline-flex items-center gap-1 text-sm font-bold text-[#f4a17f] underline underline-offset-4 hover:text-[#ffc1aa]"
      search={{ discogs: undefined, redirect: undefined }}
      to="/taste"
    >
      {t("personalize")}
      <ArrowForwardIcon className="text-lg" decorative />
    </Link>
  );
}

function BrowserPushPanel({
  alerts,
  controller,
  surface,
}: {
  alerts: boolean;
  controller: BrowserPushController;
  surface: BrowserPushControlProps["surface"];
}) {
  return (
    <div className={cn(alerts ? "pt-6 text-[#f7f3ea]" : "relative rounded-xl bg-muted/50 p-5")}>
      <BrowserPushState alerts={alerts} status={controller.status} />
      <BrowserPushSettingsSummary
        activeBrowserCount={controller.activeBrowserCount}
        status={controller.status}
        surface={surface}
      />
      <BrowserPushActions alerts={alerts} controller={controller} surface={surface} />
      <BrowserPushRecoveryActions alerts={alerts} controller={controller} />
      <PersonalizeLink alerts={alerts} status={controller.status} />
      <PwaInstallPromotion
        surface={alerts ? "alerts_page" : "notification_settings"}
        tone={alerts ? "alerts" : "settings"}
      />
    </div>
  );
}

export function BrowserPushControl({ surface }: BrowserPushControlProps) {
  const t = useTranslations("browserPush");
  const controller = useWebPushController({
    analyticsSurface: surface === "alerts" ? "alerts_page" : "notification_settings",
  });
  const alerts = surface === "alerts";
  const viewed = useRef(false);

  useEffect(() => {
    if (viewed.current || !controller.canEnable) return;
    viewed.current = true;
    trackNotificationChannelPromptViewed(
      surface === "alerts" ? "alerts_page" : "notification_settings",
    );
  }, [controller, surface]);

  if (alerts) {
    return (
      <article
        aria-labelledby="browser-push-alerts-title"
        className="flex scroll-mt-20 flex-col bg-[#171b18] p-6 sm:p-7"
        id="alerts-notifications"
      >
        <div className="flex items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-[#e66a3a] text-[#17130f]">
            <ActiveNotificationIcon className="text-2xl" decorative />
          </span>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-[#f4a17f]">
            {t("label")}
          </p>
        </div>
        <h2 className="mt-6 text-2xl font-black tracking-tight" id="browser-push-alerts-title">
          {t("title")}
        </h2>
        <p className="mt-3 max-w-xl text-sm leading-6 text-[#aaa79f]">{t("description")}</p>
        <BrowserPushPanel alerts controller={controller} surface={surface} />
        <ul className="mt-6 space-y-2">
          {POINT_KEYS.map((key) => (
            <li className="flex items-start gap-3 text-sm leading-6 text-[#d5d2ca]" key={key}>
              <CheckIcon className="mt-0.5 text-lg text-[#f4a17f]" decorative />
              <span>{t(key)}</span>
            </li>
          ))}
        </ul>
      </article>
    );
  }

  return (
    <section
      aria-labelledby={`browser-push-${surface}-title`}
      className="mb-8 overflow-hidden rounded-xl border border-border bg-card p-5 sm:p-6"
    >
      <div className="relative grid gap-7 lg:grid-cols-[minmax(0,1.35fr)_minmax(260px,.65fr)] lg:items-center">
        <div>
          <div className="flex items-center gap-3">
            <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
              <ActiveNotificationIcon className="text-2xl" decorative />
            </span>
            <h2 className="text-2xl font-black tracking-tight" id={`browser-push-${surface}-title`}>
              {t("settingsTitle")}
            </h2>
          </div>
          <p className="mt-4 max-w-2xl text-sm leading-6 text-muted-foreground">
            {t("settingsDescription")}
          </p>
        </div>

        <BrowserPushPanel alerts={false} controller={controller} surface={surface} />
      </div>
    </section>
  );
}
